import {
  Box,
  Divider,
  Drawer,
  List,
  ListSubheader,
  makeStyles,
} from "@material-ui/core"
import React, { useMemo, useState } from "react"
import PerfectScrollbar from "react-perfect-scrollbar"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"
import bgImage from "src/assets/img/nav_nachos@2x.png"
import LogoDown from "src/components/Common/LogoDown"
import useNavSections from "src/hooks/useNavSections"
import { getActiveNavItemId, hasActiveChild } from "src/navigation"
import NavItem from "./NavItem"

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  drawerPaper: {
    width: 256,
  },
  dividerColor: {
    backgroundColor: theme.palette.primary.contrastText,
  },
  navItem: {
    transition: "transform 0.1s ease",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#4b78a5",
      transform: "scale(1.03)",
    },
  },
  subMenuItem: {
    paddingLeft: theme.spacing(0),
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#3a5f91",
    },
  },
  sectionSubheader: {
    backgroundColor: "transparent",
    color: theme.palette.primary.contrastText,
    opacity: 0.6,
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1.2,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    "&:first-child": {
      paddingTop: theme.spacing(0.5),
    },
  },
  sectionList: {
    paddingTop: 0,
    paddingBottom: theme.spacing(0.5),
  },
  menuButton: {
    minWidth: 36,
    width: 36,
    height: 36,
    padding: 0,
    marginRight: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.up("sm")]: {
      minWidth: 42,
      width: 42,
      height: 42,
    },
  },
}))

const SideNav = ({ open, onClose, isDesktop }) => {
  const classes = useStyles()
  const location = useLocation()
  const visibleSections = useNavSections()
  const [expandedMenus, setExpandedMenus] = useState({})

  const activeNavItemId = useMemo(
    () =>
      getActiveNavItemId(
        visibleSections.flatMap(section => section.items),
        location.pathname
      ),
    [visibleSections, location.pathname]
  )

  const isMenuOpen = item => {
    if (expandedMenus[item.id] !== undefined) {
      return expandedMenus[item.id]
    }
    return hasActiveChild(item, location.pathname)
  }

  const handleToggleMenu = item => {
    setExpandedMenus(prev => ({
      ...prev,
      [item.id]: !isMenuOpen(item),
    }))
  }

  const renderNavItems = ({ items, pathname, depth = 0 }) => (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            pathname,
            depth,
          }),
        []
      )}
    </List>
  )

  const reduceChildRoutes = ({ acc, pathname, item, depth }) => {
    const key = `${item.id}-${depth}`
    const hasChildren = Boolean(item.items)
    const isSubMenu = depth > 0
    const isActive = item.id === activeNavItemId

    if (hasChildren) {
      acc.push(
        <NavItem
          depth={depth}
          icon={item.icon}
          info={item.info}
          key={key}
          title={item.title}
          open={isMenuOpen(item)}
          onToggle={() => handleToggleMenu(item)}
          className={classes.navItem}
        >
          {renderNavItems({ depth: depth + 1, pathname, items: item.items })}
        </NavItem>
      )
    } else {
      acc.push(
        <NavItem
          depth={depth}
          href={item.href}
          icon={item.icon}
          info={item.info}
          key={key}
          title={item.title}
          active={isActive}
          className={isSubMenu ? classes.subMenuItem : classes.navItem}
        />
      )
    }

    return acc
  }

  return (
    <Drawer
      anchor="left"
      classes={{ paper: classes.drawerPaper }}
      variant={isDesktop ? "persistent" : "temporary"}
      open={open}
      onClose={onClose}
    >
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        className={classes.root}
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <PerfectScrollbar options={{ suppressScrollX: true }}>
          <Box p={2}>
            <LogoDown />
          </Box>
          <Divider className={classes.dividerColor} variant="middle" />
          <Box p={2}>
            {visibleSections.map(section => (
              <List
                key={section.id || section.subheader}
                className={classes.sectionList}
                disablePadding
                subheader={
                  section.subheader ? (
                    <ListSubheader
                      className={classes.sectionSubheader}
                      disableGutters
                      disableSticky
                    >
                      {section.subheader}
                    </ListSubheader>
                  ) : null
                }
              >
                {renderNavItems({
                  items: section.items,
                  pathname: location.pathname,
                })}
              </List>
            ))}
          </Box>
        </PerfectScrollbar>
      </Box>
    </Drawer>
  )
}

SideNav.propTypes = {
  isDesktop: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}

export default SideNav
